import { Copy, Download, ExternalLink } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useToast } from "../../components/Toast";
import { ICON } from "../../components/ui";
import {
  downloadBlob,
  makeQr,
  QR_INK,
  QR_PNG_SIZES,
  QR_QUIET,
  type QrMatrix,
  type QrPngSize,
  qrPath,
  qrPng,
  qrSvg,
  qrTarget,
} from "../../lib/qr";

const SIZE_LABEL: Record<QrPngSize, string> = {
  512: "Mic · 512 px",
  1024: "Mediu · 1024 px",
  2048: "Mare · 2048 px",
};

export const displayUrl = (url: string) => url.replace(/^https?:\/\//, "");

/** Copies the canonical link (prototype `copyLink`); false when the browser refuses clipboard access. */
export async function copyLink(url: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
}

/** "Codul tău QR" card from the prototype QR page: the code, the link, PNG/SVG downloads, copy and share. */
export function QrCard({
  publicUrl,
  slug,
  restaurantName,
}: {
  publicUrl: string;
  slug: string;
  restaurantName: string;
}) {
  const toast = useToast();
  const titleId = useId();
  const sizeId = useId();
  const [matrix, setMatrix] = useState<QrMatrix | null>(null);
  const [failed, setFailed] = useState(false);
  const [size, setSize] = useState<QrPngSize>(1024);
  const shown = displayUrl(publicUrl);

  useEffect(() => {
    let live = true;
    makeQr(qrTarget(publicUrl)).then(
      (m) => live && setMatrix(m),
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, [publicUrl]);

  const n = matrix ? matrix.length + 2 * QR_QUIET : 0;

  async function downloadPng() {
    if (!matrix) return;
    try {
      downloadBlob(await qrPng(matrix, size), `${slug}-qr-${size}.png`);
      toast(`PNG descărcat (${size} × ${size} px).`);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  }

  function downloadSvg() {
    if (!matrix) return;
    downloadBlob(new Blob([qrSvg(matrix)], { type: "image/svg+xml" }), `${slug}-qr.svg`);
    toast("SVG descărcat. Potrivit pentru tipografie, la orice mărime.");
  }

  async function share() {
    if (navigator.share) {
      try {
        await navigator.share({ title: `Meniul ${restaurantName}`, url: publicUrl });
        return;
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return;
      }
    }
    const ok = await copyLink(publicUrl);
    toast(
      ok
        ? "Distribuirea directă nu e disponibilă aici. Linkul a fost copiat."
        : `Distribuirea directă nu e disponibilă aici. Copiază linkul: ${shown}`,
    );
  }

  return (
    <section className="card stack" aria-labelledby={titleId} style={{ gap: 16, alignItems: "flex-start" }}>
      <h2 id={titleId} className="h2">
        Codul tău QR
      </h2>
      <div className="qr-box">
        {matrix ? (
          <svg
            viewBox={`0 0 ${n} ${n}`}
            width="100%"
            height="100%"
            role="img"
            aria-label={`Cod QR către ${shown}`}
            shapeRendering="crispEdges"
          >
            <rect width={n} height={n} fill="#FFFFFF" />
            <path d={qrPath(matrix)} fill={QR_INK} />
          </svg>
        ) : (
          <span className="caption muted" style={{ textAlign: "center" }}>
            {failed ? "Codul nu a putut fi generat. Reîncarcă pagina." : "Se generează codul…"}
          </span>
        )}
      </div>
      <span style={{ fontSize: 15, fontWeight: 600, wordBreak: "break-all" }}>{shown}</span>
      <span className="note">
        Codul duce la linkul de mai sus. Scanările lui apar separat în Statistici. Linkul nu se schimbă când
        actualizezi meniul.
      </span>
      <div className="stack" style={{ gap: 6 }}>
        <span id={sizeId} className="field-label">
          Mărime PNG
        </span>
        <fieldset className="segmented sm" aria-labelledby={sizeId} style={{ margin: 0, flexWrap: "wrap" }}>
          {QR_PNG_SIZES.map((px) => (
            <button key={px} type="button" aria-pressed={size === px} onClick={() => setSize(px)}>
              {SIZE_LABEL[px]}
            </button>
          ))}
        </fieldset>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <button type="button" className="btn btn-primary" disabled={!matrix} onClick={downloadPng}>
          <Download {...ICON} aria-hidden="true" />
          Descarcă PNG
        </button>
        <button type="button" className="btn btn-secondary" disabled={!matrix} onClick={downloadSvg}>
          Descarcă SVG
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={async () =>
            toast((await copyLink(publicUrl)) ? `Link copiat: ${shown}` : `Copiază linkul manual: ${shown}`)
          }
        >
          <Copy {...ICON} aria-hidden="true" />
          Copiază linkul
        </button>
        <button type="button" className="btn btn-secondary" onClick={share}>
          <ExternalLink {...ICON} aria-hidden="true" />
          Distribuie
        </button>
      </div>
    </section>
  );
}
